import { proxy } from 'valtio';
import type { PageStore, PageState } from '../types';
import type { Page } from '@/types/page';
import type { PartialBlock } from '@/types';
import { traceBlocks } from '@/lib/utils/block-trace';
import clonePageBlocks from '@/lib/utils/clone-page-blocks';
import { randomUUID } from '@/lib/utils/uuid';
import { logger } from '@/lib/logger';
import { compareFractionalKeys } from '@/lib/order/fractional-order';

export interface PageActions {
  setPages: (pages: Record<string, Page | PageState>) => void;
  addPage: (page: Page | PageState) => void;
  updatePage: (pageId: string, updates: Partial<Page | PageState>) => void;
  deletePage: (pageId: string) => void;
  toggleExpanded: (pageId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  // 編集中コンテンツの管理
  setEditingContent: (pageId: string | null, blocks: unknown[]) => void;
  updateEditingBlocks: (blocks: unknown[], pageId?: string) => void;
  setExternalUpdate: (isExternal: boolean) => void;
  consumeExternalUpdate: () => boolean;
  // server-side AI 更新済み（非アクティブ）ページの強制再取得マーカー (#5967)
  markServerMutatedPage: (pageId: string) => void;
  consumeServerMutatedPage: (pageId: string) => boolean;
  markEditingContentSaved: () => void;
  markEditingContentDirty: () => void;
  clearEditingContent: () => void;
  // 追加のアクション（後方互換性のため）
  expandPage: (pageId: string) => void;
  setPageOrder: (order: string[]) => void;
  startEditing: (pageId: string) => void;
  stopEditing: (pageId: string) => void;
  togglePageExpansion: (pageId: string) => void;
  // ヘルパーメソッド
  wouldCreateCircularReference: (pageId: string, parentId: string) => boolean;
}

// メモ化用のWeakMap
const memoCache = new WeakMap<PageStore, {
  sortedPages?: { input: string; result: PageState[] };
  pageTree?: { input: string; result: PageState[] };
}>();

// Page型をPageState型に変換
function toPageState(page: Page | PageState): PageState {
  if ('isDeleted' in page) {
    return page as PageState;
  }

  const parentId = (page as Page).parent_id
    ?? (page as { parentId?: string | null }).parentId
    ?? null;
  const orderKey = (page as Page).order_key
    ?? (page as { orderKey?: string | null }).orderKey
    ?? null;
  const orderIndex = (page as Page).order_index
    ?? (page as { orderIndex?: number }).orderIndex
    ?? 0;
  const blocks = Array.isArray((page as { blocks?: unknown[] }).blocks)
    ? (page as { blocks?: unknown[] }).blocks
    : [];
  // 楽観的ロック用のバージョン番号を正規化後も保持する。
  // server-applied createPage 結果は serverPageVersion をここまで配管しているため、
  // 欠落すると Valtio ストアの page.version が undefined となり OCC がスキップされる (#5976)。
  const version = (page as { version?: number }).version;

  return {
    id: page.id,
    title: page.title,
    content: '',
    parentId,
    order_key: orderKey,
    order_index: orderIndex,
    createdAt: new Date(page.created_at),
    updatedAt: new Date(page.updated_at),
    isDeleted: false,
    isDeleting: false,
    isCreating: page.isCreating ?? false,
    blocks,
    version,
  };
}

export function createPageStore(initialState?: Partial<PageStore>) {
  const store = proxy<PageStore>({
    pages: {},
    pageOrder: [],
    editingPages: new Set(),
    expandedPages: new Set(),
    isLoading: false,
    error: null,
    currentEditingContent: {
      pageId: null,
      blocks: [],
      isDirty: false,
      lastSavedAt: null,
      hasReceivedEdit: false,
      isExternalUpdate: false,
    },
    serverMutatedPageIds: {},
    ...initialState,
  });

  // ヘルパー関数：循環参照チェック
  const hasCircularReference = (pageId: string, targetParentId: string): boolean => {
    if (pageId === targetParentId) return true; // 自分自身を親にする

    const visited = new Set<string>();
    let currentId: string | null = targetParentId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const currentPage = store.pages[currentId];
      if (!currentPage) break;

      if (currentPage.parentId === pageId) return true; // 循環を検出
      currentId = currentPage.parentId;
    }

    return false;
  };

  const ensureIdsForBlocks = (
    incomingBlocks: PartialBlock[],
    baseBlocks: PartialBlock[] = [],
    pathPrefix = ''
  ): PartialBlock[] => {
    return incomingBlocks.map((block, index) => {
      const baseBlock = baseBlocks[index];
      const path = pathPrefix ? `${pathPrefix}.${index}` : `${index}`;
      const result: PartialBlock = { ...block };

      if (!result.id || typeof result.id !== 'string') {
        result.id = baseBlock?.id ?? `block-${path}-${randomUUID()}`;
      }

      if (Array.isArray(result.children)) {
        const baseChildren = Array.isArray(baseBlock?.children)
          ? (baseBlock!.children as PartialBlock[])
          : [];
        result.children = ensureIdsForBlocks(
          result.children as PartialBlock[],
          baseChildren,
          path
        );
      }

      return result;
    });
  };

  const actions: PageActions = {
    setPages(pages: Record<string, Page | PageState>) {
      const convertedPages: Record<string, PageState> = {};
      for (const [id, page] of Object.entries(pages)) {
        convertedPages[id] = toPageState(page);
      }

      // 🔍 DEBUG: Log blocks before and after conversion
      if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
        const pageWithBlocks = Object.entries(pages).find(([_, p]) => p.blocks && p.blocks.length > 0);
        if (pageWithBlocks) {
          const [pageId, inputPage] = pageWithBlocks;
          const convertedPage = convertedPages[pageId];
          logger.debug('[setPages] block IDs check', {
            pageId,
            inputBlockIds: (inputPage.blocks || []).slice(0, 3).map((b: unknown, i: number) => ({
              index: i,
              id: (b as { id?: unknown })?.id,
              type: (b as { type?: unknown })?.type
            })),
            convertedBlockIds: (convertedPage.blocks || []).slice(0, 3).map((b: unknown, i: number) => ({
              index: i,
              id: (b as { id?: unknown })?.id,
              type: (b as { type?: unknown })?.type
            })),
          });
        }
      }

      store.pages = convertedPages;
      store.pageOrder = Object.keys(convertedPages);
      store.isLoading = false;
      store.error = null;
    },

    addPage(page: Page | PageState) {
      const pageState = toPageState(page);

      // 循環参照チェック
      if (pageState.parentId && hasCircularReference(pageState.id, pageState.parentId)) {
        logger.warn(`[pageStoreFactory] Circular reference detected for page ${pageState.id}, setting parentId to null`);
        pageState.parentId = null;
      }

      // 既存のページが存在する場合、新しいデータでマージして更新する（Upsert パターン）
      if (store.pages[page.id]) {
        // 既存のページ情報と新しいページ情報をマージする
        // updatedAtを現在時刻で更新し、新しいデータを優先する
        const existing = store.pages[page.id];
        const updatedPageState = {
          ...existing,
          ...pageState,
          // 楽観的ロック用 version は、incoming が未定義なら既存値を維持する。
          // 未定義で上書きすると OCC 保護が失われるため (#5976)。
          version: pageState.version ?? existing.version,
          updatedAt: new Date()
        };
        store.pages[page.id] = updatedPageState;
        logger.debug(`[pageStoreFactory] Page ${page.id} already exists. Updated with new data.`);

        // 親ページの展開処理は既存ページ更新時も実行
        if (updatedPageState.parentId && store.pages[updatedPageState.parentId]) {
          const parent = store.pages[updatedPageState.parentId];
          if (!parent.isDeleted) {
            store.expandedPages.add(updatedPageState.parentId);
          } else {
            store.expandedPages.delete(updatedPageState.parentId);
          }
        }
        return;
      }

      // 新規ページの追加処理
      store.pages[page.id] = pageState;
      if (!store.pageOrder.includes(page.id)) {
        store.pageOrder.push(page.id);
      }

      // 親ページが存在する場合、自動的に展開する
      if (pageState.parentId && store.pages[pageState.parentId]) {
        const parent = store.pages[pageState.parentId];
        if (!parent.isDeleted) {
          store.expandedPages.add(pageState.parentId);
        } else {
          store.expandedPages.delete(pageState.parentId);
        }
      }
    },

    // 循環参照をチェックするヘルパーメソッド
    wouldCreateCircularReference(pageId: string, parentId: string): boolean {
      if (parentId === pageId) {
        return true;
      }

      let current: string | null = parentId;
      const visited = new Set<string>();

      while (current && !visited.has(current)) {
        visited.add(current);
        const parent = store.pages[current];
        if (!parent) {
          break;
        }
        current = parent.parentId;
        if (current === pageId) {
          return true;
        }
      }

      return false;
    },

    updatePage(pageId: string, updates: Partial<Page | PageState>) {
      const rawUpdates = updates as Record<string, unknown>;
      const normalizedUpdates: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(rawUpdates)) {
        if (value !== undefined) {
          normalizedUpdates[key] = value;
        }
      }

      if ('parent_id' in rawUpdates && rawUpdates.parent_id !== undefined) {
        normalizedUpdates.parentId = rawUpdates.parent_id;
      }
      delete normalizedUpdates.parent_id;
      if ('orderKey' in rawUpdates && rawUpdates.orderKey !== undefined) {
        normalizedUpdates.order_key = rawUpdates.orderKey;
      }
      if ('order_key' in rawUpdates && rawUpdates.order_key !== undefined) {
        normalizedUpdates.order_key = rawUpdates.order_key;
      }
      delete normalizedUpdates.orderKey;
      if ('orderIndex' in rawUpdates && rawUpdates.orderIndex !== undefined) {
        normalizedUpdates.order_index = rawUpdates.orderIndex;
      }
      delete normalizedUpdates.orderIndex;

      const page = store.pages[pageId];
      if (page) {
        if (Object.keys(normalizedUpdates).length === 0) {
          return;
        }

        const nextParentId = normalizedUpdates.parentId as string | null | undefined;

        // 親子関係の更新時に循環参照チェック
        if (typeof nextParentId === 'string' && nextParentId.length > 0) {
          if (hasCircularReference(pageId, nextParentId)) {
            logger.warn(`[pageStoreFactory] Circular reference detected for page ${pageId}, ignoring parentId update`);
            const { parentId: _parentId, ...safeUpdates } = normalizedUpdates;
            Object.assign(page, safeUpdates);
          } else {
            Object.assign(page, normalizedUpdates);
          }
        } else {
          Object.assign(page, normalizedUpdates);
        }
        page.updatedAt = new Date();
      } else {
        // Issue #1871: ページが存在しない場合は新規追加（upsert）
        // selectPage で selectedPageId だけ先行して pagesById が空の状態を防ぐ
        //
        // 方式B: 「フルPageのときだけ upsert」（安全な upsert）
        // - 不完全なデータ（stub）がストアに混入するのを防ぐ
        // - API詳細取得結果のみを追加する
        const u = updates as Record<string, unknown>;
        const hasId = typeof u.id === 'string' && u.id === pageId;
        const hasTitle = typeof u.title === 'string';
        const hasCreatedAt = u.createdAt instanceof Date || typeof u.created_at === 'string';
        const hasUpdatedAt = u.updatedAt instanceof Date || typeof u.updated_at === 'string';
        const isFullPage = hasId && hasTitle && hasCreatedAt && hasUpdatedAt;

        if (!isFullPage) {
          // 不完全なデータは追加しない（副作用を防ぐ）
          logger.warn(`[pageStoreFactory] Page ${pageId} not in store and updates incomplete, skipping upsert`);
          return;
        }

        const now = new Date();
        const resolvedParentId = ('parentId' in normalizedUpdates
          ? normalizedUpdates.parentId
          : (normalizedUpdates as { parent_id?: string | null }).parent_id) as string | null | undefined;
        const resolvedOrderKey = (normalizedUpdates as { order_key?: string | null; orderKey?: string | null }).order_key
          ?? (normalizedUpdates as { orderKey?: string | null }).orderKey
          ?? null;
        const resolvedOrderIndex = (normalizedUpdates as { order_index?: number; orderIndex?: number }).order_index
          ?? (normalizedUpdates as { orderIndex?: number }).orderIndex
          ?? 0;

        const newPage: PageState = {
          id: pageId,
          title: (normalizedUpdates as { title?: string }).title || '',
          content: '',
          parentId: resolvedParentId ?? null,
          order_key: resolvedOrderKey,
          order_index: resolvedOrderIndex,
          createdAt: ('createdAt' in normalizedUpdates && normalizedUpdates.createdAt instanceof Date)
            ? normalizedUpdates.createdAt
            : ('created_at' in normalizedUpdates ? new Date((normalizedUpdates as { created_at?: string }).created_at || now) : now),
          updatedAt: ('updatedAt' in normalizedUpdates && normalizedUpdates.updatedAt instanceof Date)
            ? normalizedUpdates.updatedAt
            : ('updated_at' in normalizedUpdates ? new Date((normalizedUpdates as { updated_at?: string }).updated_at || now) : now),
          isDeleted: false,
          isDeleting: false,
          isCreating: false,
          blocks: (normalizedUpdates as { blocks?: unknown[] }).blocks || [],
          version: (normalizedUpdates as { version?: number }).version,
        };
        store.pages[pageId] = newPage;
        if (!store.pageOrder.includes(pageId)) {
          store.pageOrder.push(pageId);
        }
        logger.info(`[pageStoreFactory] Page ${pageId} upserted (was missing from store)`);
      }
    },

    deletePage(pageId: string) {
      // ソフト削除を実装 - isDeletedフラグを立てる
      if (store.pages[pageId]) {
        const deletedIds = new Set<string>();
        const markDeleted = (id: string) => {
          const page = store.pages[id];
          if (!page) return;
          page.isDeleted = true;
          deletedIds.add(id);
        };

        markDeleted(pageId);

        // 子ページも再帰的に削除（循環参照対策付き）
        const visited = new Set<string>();
        const deleteChildren = (parentId: string) => {
          if (visited.has(parentId)) {
            logger.warn(`[pageStoreFactory] Circular reference detected in deletion, skipping ${parentId}`);
            return;
          }
          visited.add(parentId);

          for (const id of store.pageOrder) {
            const page = store.pages[id];
            if (page && page.parentId === parentId && !page.isDeleted) {
              markDeleted(id);
              deleteChildren(id);
            }
          }
        };
        deleteChildren(pageId);

        // 編集・展開状態からも削除（子孫含む）
        for (const id of Array.from(store.editingPages)) {
          if (deletedIds.has(id) || store.pages[id]?.isDeleted) {
            store.editingPages.delete(id);
          }
        }
        for (const id of Array.from(store.expandedPages)) {
          if (deletedIds.has(id) || store.pages[id]?.isDeleted) {
            store.expandedPages.delete(id);
          }
        }
      }
    },

    toggleExpanded(pageId: string) {
      const page = store.pages[pageId];
      if (!page || page.isDeleted) {
        store.expandedPages.delete(pageId);
        return;
      }
      const newSet = new Set(store.expandedPages);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      store.expandedPages = newSet;
    },

    setLoading(isLoading: boolean) {
      store.isLoading = isLoading;
    },

    setError(error: string | null) {
      store.error = error;
      store.isLoading = false;
    },

    clearError() {
      store.error = null;
    },

    setEditingContent(pageId: string | null, blocks: unknown[]) {
      // 不変条件を維持: pageIdがnullの場合はblocksは空にする
      const validatedBlocks = pageId === null ? [] : [...blocks];
      traceBlocks('pageStore.setEditingContent.input', blocks, { pageId });

      if (pageId === null && blocks.length > 0) {
        logger.warn('[pageStoreFactory] setEditingContent: pageId is null but blocks provided, clearing blocks to maintain invariant');
      }

      const isNewPage = pageId === null;
      const preserveExternalUpdate =
        store.currentEditingContent.isExternalUpdate &&
        store.currentEditingContent.pageId === pageId;

      store.currentEditingContent = {
        pageId,
        blocks: validatedBlocks, // 配列をコピーして不変性を保つ
        isDirty: false, // 元の実装：常にfalse（編集セッション開始時はclean）
        lastSavedAt: isNewPage ? null : new Date(), // 新規ページはnull、既存ページは現在時刻
        hasReceivedEdit: false, // 新しいコンテンツ設定時はfalse
        isExternalUpdate: preserveExternalUpdate,
      };

      traceBlocks('pageStore.setEditingContent.stored', store.currentEditingContent.blocks, { pageId });
    },

    updateEditingBlocks(blocks: unknown[], pageId?: string) {
      // pageIdが提供された場合、またはcurrentEditingContent.pageIdが存在する場合
      const targetPageId = pageId || store.currentEditingContent.pageId;

      if (targetPageId) {
        // pageIdが異なる場合は、新しいページとして設定
        if (store.currentEditingContent.pageId !== targetPageId) {
          store.currentEditingContent.pageId = targetPageId;
        }

        const incomingBlocks = Array.isArray(blocks) ? clonePageBlocks(blocks) : [];
        const baseBlocks = Array.isArray(store.currentEditingContent.blocks)
          ? clonePageBlocks(store.currentEditingContent.blocks)
          : [];
        const blocksWithIds = ensureIdsForBlocks(incomingBlocks, baseBlocks);

        store.currentEditingContent.blocks = blocksWithIds;
        store.currentEditingContent.isDirty = true;
        store.currentEditingContent.hasReceivedEdit = true; // EDITイベントを受信したことを記録
        // isExternalUpdateフラグはそのまま保持（外部で設定されている場合がある）
        traceBlocks('pageStore.updateEditingBlocks.applied', store.currentEditingContent.blocks, {
          pageId: targetPageId,
        });
      } else {
        // pageIdがない場合は、編集コンテンツをクリアする
        // これにより不変条件「pageIdがnullならblocksは空」が保たれる
        if (blocks.length > 0) {
          logger.warn('[pageStoreFactory] updateEditingBlocks called without pageId, clearing blocks to maintain invariant');
        }
        store.currentEditingContent.pageId = null;
        store.currentEditingContent.blocks = [];
        store.currentEditingContent.isDirty = false;
        store.currentEditingContent.hasReceivedEdit = false;
        traceBlocks('pageStore.updateEditingBlocks.cleared', store.currentEditingContent.blocks, {
          pageId: targetPageId ?? null,
        });
      }
    },

    setExternalUpdate(isExternal: boolean) {
      store.currentEditingContent.isExternalUpdate = isExternal;
    },
    consumeExternalUpdate() {
      const shouldSkip = store.currentEditingContent.isExternalUpdate === true;
      if (shouldSkip) {
        store.currentEditingContent.isExternalUpdate = false;
      }
      return shouldSkip;
    },

    // #5967: server-side で AI 更新された非アクティブページを記録する。
    // WS broadcast が届かないため、次回 selectPage 時に強制再取得させる。
    markServerMutatedPage(pageId: string) {
      if (!pageId) return;
      store.serverMutatedPageIds[pageId] = true;
    },
    // #5967: マーカーが立っていれば true を返して消費（削除）する。一度きり。
    consumeServerMutatedPage(pageId: string) {
      const wasMutated = store.serverMutatedPageIds[pageId] === true;
      if (wasMutated) {
        delete store.serverMutatedPageIds[pageId];
      }
      return wasMutated;
    },

    markEditingContentSaved() {
      // 不変条件を保つ: 保存時はpageIdがあるはず
      if (store.currentEditingContent.pageId) {
        // Valtioの特性を活かして直接変更
        store.currentEditingContent.isDirty = false;
        store.currentEditingContent.lastSavedAt = new Date();
      } else {
        logger.warn('[pageStoreFactory] markEditingContentSaved called without pageId');
      }
    },

    markEditingContentDirty() {
      // 編集内容が変更されたことをマーク
      if (store.currentEditingContent.pageId) {
        store.currentEditingContent.isDirty = true;
      }
    },

    clearEditingContent() {
      // Valtioの特性を活かして直接変更
      store.currentEditingContent.pageId = null;
      store.currentEditingContent.blocks = [];
      store.currentEditingContent.isDirty = false;
      store.currentEditingContent.lastSavedAt = null;
      store.currentEditingContent.hasReceivedEdit = false;
    },

    // 追加のアクション（後方互換性のため）
    expandPage(pageId: string) {
      if (!store.expandedPages.has(pageId)) {
        store.expandedPages.add(pageId);
      }
    },

    setPageOrder(order: string[]) {
      store.pageOrder = order;
    },

    startEditing(pageId: string) {
      const page = store.pages[pageId];
      if (!page || page.isDeleted) {
        store.editingPages.delete(pageId);
        return;
      }
      if (!store.editingPages.has(pageId)) {
        store.editingPages.add(pageId);
      }
    },

    stopEditing(pageId: string) {
      if (store.editingPages.has(pageId)) {
        store.editingPages.delete(pageId);
      }
    },

    togglePageExpansion(pageId: string) {
      this.toggleExpanded(pageId);
    },
  };

  // 派生状態の定義 - メモ化付きgetter関数として実装
  const derived = {
    get sortedPages() {
      const cacheKey = store.pageOrder.join(',');
      const cached = memoCache.get(store);

      if (cached?.sortedPages?.input === cacheKey) {
        return cached.sortedPages.result;
      }

      const result = store.pageOrder
        .map(id => store.pages[id])
        .filter(page => page !== undefined && !page.isDeleted) // 削除済みを除外
        .sort((a, b) => {
          const orderKeyA = typeof a.order_key === 'string' ? a.order_key : '';
          const orderKeyB = typeof b.order_key === 'string' ? b.order_key : '';
          if (orderKeyA && orderKeyB) {
            const keyDiff = compareFractionalKeys(orderKeyA, orderKeyB);
            if (keyDiff !== 0) {
              return keyDiff;
            }
          } else if (orderKeyA) {
            return -1;
          } else if (orderKeyB) {
            return 1;
          }
          const createdAtA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
          const createdAtB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
          const createdAtDiff = createdAtA - createdAtB;
          if (createdAtDiff !== 0) {
            return createdAtDiff;
          }
          return a.id.localeCompare(b.id);
        });

      if (!memoCache.has(store)) {
        memoCache.set(store, {});
      }
      const cache = memoCache.get(store)!;
      cache.sortedPages = { input: cacheKey, result };

      return result;
    },

    get pageTree() {
      const cacheKey = store.pageOrder.join(',');
      const cached = memoCache.get(store);

      if (cached?.pageTree?.input === cacheKey) {
        return cached.pageTree.result;
      }

      const comparePageOrder = (a: PageState, b: PageState) => {
        const orderKeyA = typeof a.order_key === 'string' ? a.order_key : '';
        const orderKeyB = typeof b.order_key === 'string' ? b.order_key : '';
        if (orderKeyA && orderKeyB) {
          const keyDiff = compareFractionalKeys(orderKeyA, orderKeyB);
          if (keyDiff !== 0) {
            return keyDiff;
          }
        } else if (orderKeyA) {
          return -1;
        } else if (orderKeyB) {
          return 1;
        }
        const createdAtA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const createdAtB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        const createdAtDiff = createdAtA - createdAtB;
        if (createdAtDiff !== 0) {
          return createdAtDiff;
        }
        return a.id.localeCompare(b.id);
      };

      // ルートページを見つける（削除済みを除外）
      const rootPages = store.pageOrder
        .map(id => store.pages[id])
        .filter(page => page && !page.parentId && !page.isDeleted)
        .sort(comparePageOrder);

      // 子ページを再帰的に構築（削除済みを除外）
      const buildTree = (parentId: string | null): PageState[] => {
        return store.pageOrder
          .map(id => store.pages[id])
          .filter(page => page && page.parentId === parentId && !page.isDeleted)
          .sort(comparePageOrder)
          .map(page => ({
            ...page,
            children: buildTree(page.id),
          }));
      };

      const result = rootPages.map(page => ({
        ...page,
        children: buildTree(page.id),
      }));

      if (!memoCache.has(store)) {
        memoCache.set(store, {});
      }
      const cache = memoCache.get(store)!;
      cache.pageTree = { input: cacheKey, result };

      return result;
    },

    get pageCount() {
      return store.pageOrder.length;
    },

    get hasPages() {
      return store.pageOrder.length > 0;
    },
  };

  return { store, actions, derived };
}

export type PageStoreInstance = ReturnType<typeof createPageStore>;
